import { type ReactElement } from 'react';

import { Sheet } from '@/components/ui/sheet';
import { useI18n } from '@/i18n';
import { hapticImpact } from '@/lib/haptic';
import { QUICK_CREATE_PRESETS } from '@/lib/quick-create-reminder';
import { cn } from '@/lib/utils';

interface QuickCreateSheetProps {
  open: boolean;
  onClose: () => void;
  onSelect: (presetId: string) => void;
}

export function QuickCreateSheet({
  open,
  onClose,
  onSelect,
}: QuickCreateSheetProps): ReactElement | null {
  const { t } = useI18n();

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t('quickCreate.title')}
    >
      <p className="caption mb-3">{t('quickCreate.subtitle')}</p>
      <div className="space-y-1">
        {QUICK_CREATE_PRESETS.map((preset) => {
          const Icon = preset.icon;
          return (
            <button
              key={preset.id}
              type="button"
              className={cn(
                'tap-target flex w-full items-center gap-3 rounded-[var(--radius-md)] px-3 py-3 text-left transition-colors active:scale-[0.99]',
                preset.id === 'now'
                  ? 'bg-primary/10 text-foreground'
                  : 'text-foreground hover:bg-secondary',
              )}
              onClick={() => {
                hapticImpact('light');
                onSelect(preset.id);
              }}
            >
              <div
                className={cn(
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                  preset.id === 'now'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary text-primary',
                )}
              >
                <Icon
                  className="h-5 w-5"
                  aria-hidden
                />
              </div>
              <span className="text-[15px] font-medium">{t(preset.labelKey)}</span>
            </button>
          );
        })}
      </div>
    </Sheet>
  );
}
